import { useEffect, useRef, useState } from "react";
import NavbarItemsPermissionsPanel from "./navbar/NavbarItemsPermissionsPanel";
import AddCCAccessLevelRoleDialog from "./add-role/AddCCAccessLevelRoleDialog";
import PagesPermissionsPanel from "./pages/PagesPermissionsPanel";
import useAxios from "../../../../../../../../hooks/useAxios";
import useLoading from "../../../../../../../../shared/components/loading/useLoading";
import useToastWrapper from "../../../../../../../../hooks/useToastWrapper";
import useConfirmDialog from "../../../../../../../../shared/components/confirm-dialog/useConfirmDialog";
import { SECURITY_CENTRE_PERMISSIONS_CONTROLCENTRE } from "../../../../../../../../shared/lib/routesResolver";
import {
  API_SECURITY_CENTRE_ACCESSLEVEL,
  API_SECURITY_CENTRE_ACCESSLEVEL_BY_ID,
  API_SECURITY_CENTRE_DROPDOWN_ROELS,
  API_SECURITY_CENTRE_NAVBAR_SECTIONS,
  API_SECURITY_CENTRE_PERMISSIONS,
  API_SECURITY_CENTRE_PERMISSIONS_BY_ID,
  API_SECURITY_CENTRE_ROUTES,
} from "../../../../../../../../shared/lib/apiEndpointsResolver";
import { catchFunction } from "../../../../../../../../shared/lib/utils";
import OverviewTabContent from "../../../../../../../../shared/components/overview-panel/OverviewTabContent";
import BreadcrumbPanel from "../../../../../../../../shared/components/breadcrumb/BreadcrumbPanel";
import OverviewTabs from "../../../../../../../../shared/components/overview-panel/OverviewTabs";

const CCAccessLevelPermissionsOverview = () => {
  const axios = useAxios();
  const { setLoading } = useLoading();
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const navbarSections = useRef([]);
  const routes = useRef([]);
  const toast = useToastWrapper();
  const [permissionForDeletion, setPermissionForDeletion] = useState();
  const deletePermissionDialog = useConfirmDialog();
  const breadcrumbItems = [
    {
      title: "Permisos Control Centre",
      link: SECURITY_CENTRE_PERMISSIONS_CONTROLCENTRE,
    },
    { title: "Access level" },
  ];

  const findPermissions = () => {
    setLoading(true);
    axios
      .get(`${API_SECURITY_CENTRE_PERMISSIONS}?app=control-centre-service`)
      .then((response) => {
        const data = response.data
          .filter((x) => x.key === "role")
          .map((x) => {
            return { ...x, value: JSON.parse(x.value) };
          });
        setPermissions(data);
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const findRolesDropdown = () => {
    axios
      .get(API_SECURITY_CENTRE_DROPDOWN_ROELS)
      .then((response) => setRoles(response.data))
      .catch(catchFunction);
  };

  const findNavbarSections = () => {
    axios
      .get(API_SECURITY_CENTRE_NAVBAR_SECTIONS)
      .then((response) => (navbarSections.current = response.data))
      .catch(catchFunction);
  };

  const findRoutes = () => {
    axios
      .get(API_SECURITY_CENTRE_ROUTES)
      .then((response) => (routes.current = response.data))
      .catch(catchFunction);
  };

  const onRoleAddtion = (formValues) => {
    setLoading(true);
    const request = {
      role: formValues.role,
      navbarSections: navbarSections.current.map((x) => {
        return { ...x, status: "STABLE", hasAccess: false };
      }),
      routes: routes.current.map((x) => {
        return {
          ...x,
          hasAccess: false,
          canCreate: false,
          canEdit: false,
          canDelete: false,
        };
      }),
    };
    axios
      .post(API_SECURITY_CENTRE_ACCESSLEVEL, request)
      .then((response) => {
        toast.success(
          `Permisos de acceso para el rol ${response.data.role.name} en Control Centre creados correctamente`,
        );
        findPermissions();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onPermissionDeletion = () => {
    setLoading(true);
    axios
      .delete(API_SECURITY_CENTRE_PERMISSIONS_BY_ID(permissionForDeletion.id))
      .then((_) => {
        toast.success(`Permiso borrado correctamente`);
        findPermissions();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onPermissionDeletionClicked = (id) => {
    const selectedPermission = permissions.filter((x) => x.id === id)[0];
    setPermissionForDeletion(selectedPermission);
    deletePermissionDialog.display(
      `Confirmar el borrado del permiso para el rol ${selectedPermission.role.name}`,
    );
  };

  const onPagePermissionEditionHandler = (id, config, formValues) => {
    const routes = config.routes;
    const index = routes.findIndex((x) => x.path === formValues.path);
    routes[index] = formValues;

    updatePermissions(id, { ...config, routes: routes });
  };

  const onNavbarItemPermissionEditionHandler = (id, config, formValues) => {
    const navbarSections = config.navbarSections;
    const index = navbarSections.findIndex((x) => x.code === formValues.code);
    navbarSections[index] = formValues;

    updatePermissions(id, { ...config, navbarSections: navbarSections });
  };

  const updatePermissions = (id, request) => {
    setLoading(true);
    axios
      .put(API_SECURITY_CENTRE_ACCESSLEVEL_BY_ID(id), request)
      .then((response) => {
        toast.success(
          `Permiso de acceso para el rol ${response.data.role.name} actualizado correctamente`,
        );
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const permissionsTabs = [
    {
      trigger: { value: "navbar", label: "Barra navegación" },
      content: (
        <OverviewTabContent
          value="navbar"
          title="Permisos de visualización barra de navegación"
          description="Permisos para visualizar los distintos menús de la barra superior de navegación del Control Centre agrupados por roles"
        >
          <NavbarItemsPermissionsPanel
            data={permissions}
            onDeletionHandler={onPermissionDeletionClicked}
            onEditionHandler={onNavbarItemPermissionEditionHandler}
          />
        </OverviewTabContent>
      ),
    },
    {
      trigger: { value: "accessLevel", label: "Nivel de accesos" },
      content: (
        <OverviewTabContent
          value="accessLevel"
          title="Permisos de visualización páginas"
          description="Permisos para visualizar las páginas del Control Centre y para determinar el nivel de acceso dentro de cada una de ellas agrupados por roles"
        >
          <PagesPermissionsPanel
            data={permissions}
            onDeletionHandler={onPermissionDeletionClicked}
            onEditionHandler={onPagePermissionEditionHandler}
          />
        </OverviewTabContent>
      ),
    },
  ];

  useEffect(() => {
    findPermissions();
    findRolesDropdown();
    findNavbarSections();
    findRoutes();
  }, []);
  return (
    <>
      {deletePermissionDialog.renderDialog(onPermissionDeletion)}
      <div className="px-4 pb-4 sm:px-6">
        <BreadcrumbPanel items={breadcrumbItems} />
      </div>
      <div className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
        <OverviewTabs
          tabs={permissionsTabs}
          defaultTabValue="navbar"
          options={
            <AddCCAccessLevelRoleDialog
              onSubmitHandler={onRoleAddtion}
              roles={roles}
            />
          }
        />
      </div>
    </>
  );
};

export default CCAccessLevelPermissionsOverview;
